/**
 * settingsHistory — when the history work of a closing settings page is done.
 *
 * A settings page that closes in the page gives its history entry back
 * (useSettingsDialogUrl): a tick after it unmounts it rewrites the entry and
 * usually steps back off it, and that history.back() lands asynchronously
 * (popstate). Anything that moves next — opening another settings page,
 * leaving for a dashboard, asking the page underneath to close too — waits
 * until both have happened:
 *
 *  - a page opened before the step back lands would read that popstate as
 *    Back and close itself;
 *  - a view pushed before it lands is undone by it;
 *  - and when two pages are stacked (a location's settings opened from its
 *    account's), the page underneath must not let go before the one on top
 *    has stepped back onto its entry, or the underneath page's entry is left
 *    behind naming it, and landing on it reopens it.
 *
 * There is no timeout: a timer cannot tell a late popstate from a missing one,
 * and moving early is the bug. The step back always starts from an entry
 * useSettingsDialogUrl pushed in the same document, so the browser always
 * fires popstate.
 */

let backPending = false;
let releasesPending = 0;
let idleWaiters: Array<() => void> = [];

function isBusy(): boolean {
  return backPending || releasesPending > 0;
}

function runWaitersIfIdle(): void {
  if (isBusy() || idleWaiters.length === 0) return;
  const waiters = idleWaiters;
  idleWaiters = [];
  // A new task: the page's own popstate handling (its URL sync) commits first,
  // so it cannot strip the target of a dialog that is only just opening.
  setTimeout(() => waiters.forEach((run) => run()), 0);
}

function settleBack(): void {
  if (!backPending) return;
  backPending = false;
  window.removeEventListener('popstate', settleBack);
  runWaitersIfIdle();
}

/** Step back off a settings entry; waiters run once the step has landed. */
export function stepBackOffSettingsEntry(): void {
  if (!backPending) window.addEventListener('popstate', settleBack);
  backPending = true;
  window.history.back();
}

/**
 * A closed page's entry is about to be given back (useSettingsDialogUrl
 * schedules it a tick after unmount). Call the returned function once that is
 * done, or called off (the page opened again at once, as under StrictMode).
 */
export function holdSettingsHistory(): () => void {
  releasesPending += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    // Never below zero (a test reset can come between a hold and its release).
    releasesPending = Math.max(0, releasesPending - 1);
    runWaitersIfIdle();
  };
}

/**
 * Run `fn` once any settings page that is closing has given its history entry
 * back and finished stepping off it. Waits a tick first, so a close scheduled
 * in the same turn (the page just unmounted) gets to start.
 */
export function whenSettingsHistoryIdle(fn: () => void): void {
  setTimeout(() => {
    if (isBusy()) idleWaiters.push(fn);
    else fn();
  }, 0);
}

/** Tests only. */
export function resetSettingsHistoryIdle(): void {
  window.removeEventListener('popstate', settleBack);
  backPending = false;
  releasesPending = 0;
  idleWaiters = [];
}
